/**
 * PreparedPostsPanel — „Das ist bereits fertig."
 *
 * Zeigt die Beiträge, die für den angezeigten Monat schon erstellt wurden, als
 * kompakte Tagesliste. Bewusst KEIN zweiter Kalender: das Kalender-Grid ist auf
 * schmalen Geräten (360px) unlesbar, hier ist die Liste die verlässliche Sicht.
 *
 * Reine Darstellung — keine eigenen Netzwerk-Calls, alles über Props.
 *
 * Props:
 *   posts        — Array VOLLER Post-Objekte des Monats (Fragmente aus dem
 *                  Kalender reichen nicht: die Vorschau braucht core_content & Co.)
 *   month, year  — angezeigter Monat (1–12) / Jahr, nur für die Überschrift
 *   onPreview    — (post) => void, Klick auf Datum/Uhrzeit
 *   onAddPost    — (dateStr "YYYY-MM-DD") => void, Klick auf „+" (Tagesgruppe)
 *                  oder auf die Kopfzeilen-Aktion (Standarddatum, s. unten)
 *   onRegenerate — () => void, unauffällige Sekundäraktion „Plan neu erstellen"
 */

import { useState, useMemo } from "react";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { Plus, RefreshCw, ChevronDown } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";

// Relative Medienpfade (/api/media/file/…?t=…) gegen den Backend-Host auflösen —
// dieselbe Regel wie überall im Frontend (REACT_APP_BACKEND_URL). Ein relativer Pfad
// löste sonst gegen die Frontend-Domain auf und liefe ins Leere. Bereits absolute
// URLs (https:, data:, blob:) bleiben unangetastet.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaSrc = (url) =>
  !url ? "" : /^(https?:|data:|blob:)/i.test(url) ? url : `${BACKEND}${url}`;

const MONTH_NAMES_DE = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const MONTH_NAMES_EN = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Plattform-Badges — gleiche Farbsprache wie im Monatsplan-Kalender. Plattformnamen
// sind Eigennamen und werden nicht übersetzt.
const PLATFORM_BADGE = {
  facebook:        { label: "Facebook",  cls: "bg-blue-100 text-blue-700" },
  instagram:       { label: "Instagram", cls: "bg-pink-100 text-pink-700" },
  linkedin:        { label: "LinkedIn",  cls: "bg-sky-100 text-sky-700" },
  tiktok:          { label: "TikTok",    cls: "bg-stone-200 text-stone-700" },
  threads:         { label: "Threads",   cls: "bg-stone-900 text-white" },
  google_business: { label: "Google",    cls: "bg-orange-100 text-orange-700" },
  bluesky:         { label: "Bluesky",   cls: "bg-cyan-100 text-cyan-700" },
  pinterest:       { label: "Pinterest", cls: "bg-red-100 text-red-700" },
};

// ── Karten-Rollen (Vertrag R1) ─────────────────────────────────────────────
// Kundenorientiertes Rollen-Vokabular für Karussell-Karten, DE/EN.
const CARD_ROLE_LABELS = {
  hook:    { de: "Hook",           en: "Hook" },
  context: { de: "Kontext",        en: "Context" },
  body:    { de: "Folie",          en: "Slide" },
  summary: { de: "Fazit",          en: "Takeaway" },
  cta:     { de: "Call-to-Action", en: "Call to action" },
  single:  { de: "Bild",           en: "Image" },
};

// card_roles[] → kurze Zusammenfassung „Hook · 3 Folien · Call-to-Action" (R4).
// Inhaltsfolien (body) werden zu „N Folien" zusammengezogen. Fehlt card_roles,
// leerer String → der Aufrufer fällt sauber auf die reine Kartenzahl zurück.
// `t` wird durchgereicht, weil diese Funktion außerhalb der Komponente steht und
// keinen eigenen Hook-Zugriff hat.
const summarizeCardRoles = (cardRoles, total, t) => {
  const roles = Array.isArray(cardRoles) ? cardRoles.slice(0, total) : [];
  if (!roles.length) return "";
  const parts = [];
  let bodyRun = 0;
  const flush = () => {
    if (bodyRun > 0) {
      parts.push(bodyRun === 1 ? t("1 Folie", "1 slide") : t(`${bodyRun} Folien`, `${bodyRun} slides`));
      bodyRun = 0;
    }
  };
  roles.forEach((r) => {
    if (r === "body") { bodyRun += 1; return; }
    flush();
    const label = CARD_ROLE_LABELS[r];
    parts.push(label ? t(label.de, label.en) : t("Karte", "Card"));
  });
  flush();
  return parts.join(" · ");
};

// Vier Kunden-Kübel statt acht interner Status. „done"/„ready"/„scheduled" haben
// für den Kunden keine eigene Bedeutung und werden dem nächstliegenden Kübel zugeschlagen.
const STATUS_BUCKETS = [
  { key: "draft",     labelDe: "Entwurf",        labelEn: "Draft",        dot: "bg-stone-300",  match: ["draft"] },
  { key: "review",    labelDe: "Zur Freigabe",   labelEn: "For approval", dot: "bg-amber-400",  match: ["review"] },
  { key: "approved",  labelDe: "Freigegeben",    labelEn: "Approved",     dot: "bg-green-400",  match: ["approved", "ready", "scheduled"] },
  { key: "published", labelDe: "Veröffentlicht", labelEn: "Published",    dot: "bg-green-600",  match: ["published", "done"] },
];

const bucketOf = (status) =>
  STATUS_BUCKETS.find(b => b.match.includes(status)) || STATUS_BUCKETS[0];

const cut = (text, max) => (text.length > max ? text.slice(0, max).trimEnd() + "…" : text);

// Ein Post ohne Hook kommt vom Backend als reines "..." an (die Kürzung hängt die
// Punkte bedingungslos an). Solche Platzhalter hier abfangen, sonst stünde in der
// Liste eine Zeile aus drei Punkten. `t` wird durchgereicht (siehe summarizeCardRoles).
const titleOf = (post, t) => {
  const raw = String(post?.hook_full || post?.hook || "").trim();
  if (raw && !/^[.…\s]+$/.test(raw)) return cut(raw, 70);
  const body = String(
    post?.core_content || post?.content || post?.body || post?.text_content || ""
  ).trim();
  if (body) return cut(body, 70);
  return t("Ohne Titel", "Untitled");
};

// Keine Uhrzeit erfinden: fehlt beides, bleibt die Zelle leer.
const timeOf = (post) => String(post?.scheduled_time || post?.time || "").trim();

const dateOf = (post) => String(post?.scheduled_date || post?.date || "").trim();

// "YYYY-MM-DD" → lokales Date (ohne "T00:00:00" liest der Browser UTC → Tagesversatz).
const parseDay = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

export default function PreparedPostsPanel({
  posts,
  month,
  year,
  onPreview,
  onAddPost,
  onRegenerate,
}) {
  const { t, language } = useLanguage();
  const dateLocale = language === "en" ? enUS : de;
  const [showAll, setShowAll] = useState(false);

  // Nach Tag gruppieren; Posts ohne Datum landen in einer eigenen Gruppe am Ende.
  const groups = useMemo(() => {
    const map = new Map();
    (posts || []).forEach(p => {
      const key = dateOf(p);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    const entries = Array.from(map.entries());
    entries.forEach(([, list]) => list.sort((a, b) => timeOf(a).localeCompare(timeOf(b))));
    return entries.sort((a, b) => {
      if (!a[0]) return 1;
      if (!b[0]) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [posts]);

  const counts = useMemo(() => {
    const acc = {};
    (posts || []).forEach(p => {
      const b = bucketOf(p?.status);
      acc[b.key] = (acc[b.key] || 0) + 1;
    });
    return acc;
  }, [posts]);

  if (!Array.isArray(posts) || posts.length === 0) return null;

  // Monatsname bevorzugt aus der Prop, ersatzweise aus dem ersten datierten Post —
  // eine Überschrift „Dein Plan für  steht" wäre schlimmer als gar keine Monatsangabe.
  const monthNames = language === "en" ? MONTH_NAMES_EN : MONTH_NAMES_DE;
  const firstDated = groups.find(([day]) => day)?.[0] || "";
  const monthLabel =
    monthNames[Number(month)] || monthNames[Number(firstDated.slice(5, 7))] || "";
  const todayStr = format(new Date(), "yyyy-MM-dd");

  // Standarddatum für die Kopfzeilen-Aktion: heute, wenn der angezeigte Monat der
  // laufende ist — sonst der Monatserste. So kommt der Kunde auch an einem Tag OHNE
  // Beiträge direkt aus dem Panel heraus zum eigenen Beitrag (die Tagesgruppen-„+"
  // gibt es nur bei Tagen, die schon Beiträge haben). Ein Datum außerhalb des
  // angezeigten Monats würde die Seite verwerfen — deshalb hier immer im Monat bleiben.
  const now = new Date();
  const addYear  = Number(year)  || now.getFullYear();
  const addMonth = Number(month) || now.getMonth() + 1;
  const isCurrentMonth =
    addYear === now.getFullYear() && addMonth === now.getMonth() + 1;
  const defaultAddDate = isCurrentMonth
    ? todayStr
    : `${addYear}-${String(addMonth).padStart(2, "0")}-01`;

  // Bei vielen Tagen zuerst nur die kommenden zeigen — der Rest ist Vergangenheit
  // und drängt sonst das Aktuelle nach unten.
  const upcoming = groups.filter(([day]) => day && day >= todayStr);
  const collapsible = groups.length > 10 && upcoming.length > 0 && upcoming.length < groups.length;
  const visibleGroups = collapsible && !showAll ? upcoming : groups;

  return (
    <div
      data-testid="prepared-posts-panel"
      className="bg-white rounded-xl border border-stone-200 overflow-hidden"
    >
      {/* ── Kopfzeile ── */}
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4 border-b border-stone-100 bg-emerald-50/60">
        <div>
          <p className="font-heading font-bold text-stone-900">
            {monthLabel
              ? t(`Dein Plan für ${monthLabel}${year ? ` ${year}` : ""} steht 🦊`,
                  `Your plan for ${monthLabel}${year ? ` ${year}` : ""} is ready 🦊`)
              : t("Dein Plan steht 🦊", "Your plan is ready 🦊")}
          </p>
          <p className="text-sm text-stone-600 mt-0.5">
            {t(
              `${posts.length} ${posts.length === 1 ? "Beitrag" : "Beiträge"} vorbereitet`,
              `${posts.length} ${posts.length === 1 ? "post" : "posts"} prepared`,
            )}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            {STATUS_BUCKETS.filter(b => counts[b.key]).map(b => (
              <span key={b.key} className="inline-flex items-center gap-1.5 text-xs text-stone-500">
                <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                {counts[b.key]} {t(b.labelDe, b.labelEn)}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button
            onClick={() => onAddPost?.(defaultAddDate)}
            data-testid="prepared-plan-add-post"
            title={t("Eigenen Beitrag anlegen — auch an einem Tag ohne Beiträge", "Create your own post — even on a day with no posts yet")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-fox-fire hover:underline underline-offset-2 transition-colors"
          >
            <Plus size={13} />
            {t("Eigenen Beitrag hinzufügen", "Add your own post")}
          </button>
          <button
            onClick={onRegenerate}
            data-testid="prepared-plan-regenerate"
            className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-600 underline-offset-2 hover:underline transition-colors"
          >
            <RefreshCw size={12} />
            {t("Plan neu erstellen", "Regenerate plan")}
          </button>
        </div>
      </div>

      {/* ── Tagesliste ── */}
      <div className="divide-y divide-stone-100">
        {visibleGroups.map(([day, list]) => {
          const d = day ? parseDay(day) : null;
          return (
            <div key={day || "ohne-datum"} className="px-5 py-3">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">
                {d
                  ? format(d, language === "en" ? "EEEE, MMMM d" : "EEEE, d. MMMM", { locale: dateLocale })
                  : t("Ohne Datum", "No date")}
              </p>

              <div className="space-y-1.5">
                {list.map((post, i) => {
                  const b = bucketOf(post?.status);
                  const postTime = timeOf(post);
                  const pf = PLATFORM_BADGE[post?.platform || post?.channel] || null;
                  // Erste Karte als Miniatur; Karussells tragen card_urls[], Einzelbilder
                  // nur image_url/media_url. cardCount speist den dezenten „N Karten"-Hinweis.
                  const firstCard = post?.card_urls?.[0] || post?.image_url || post?.media_url || null;
                  const cardCount = Array.isArray(post?.card_urls) ? post.card_urls.length : 0;
                  // Rollen-Zusammenfassung fürs Tooltip (R4): „Hook · 3 Folien · Call-to-Action".
                  // Fehlt card_roles, bleibt der bisherige Text „N Karten".
                  const roleSummary = cardCount > 1 ? summarizeCardRoles(post?.card_roles, cardCount, t) : "";
                  const cardHint = cardCount > 1
                    ? (roleSummary || t(`${cardCount} Karten`, `${cardCount} cards`))
                    : t("Bild hinterlegt", "Image attached");
                  return (
                    <div
                      key={post?.id || `${day}-${i}`}
                      className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-stone-50 transition-colors"
                    >
                      <button
                        onClick={() => onPreview?.(post)}
                        data-testid="prepared-post-date"
                        title={t("Vorschau dieses Beitrags öffnen", "Open preview for this post")}
                        className="flex-shrink-0 text-left text-xs font-semibold text-fox-fire hover:underline underline-offset-2 tabular-nums"
                      >
                        {d ? format(d, language === "en" ? "MM/dd" : "dd.MM.", { locale: dateLocale }) : "—"}
                        {postTime && <span className="text-stone-500 font-normal"> · {postTime}</span>}
                      </button>

                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${b.dot}`}
                        title={t(b.labelDe, b.labelEn)}
                        aria-label={t(b.labelDe, b.labelEn)}
                      />

                      <button
                        onClick={() => onPreview?.(post)}
                        className="min-w-0 flex-1 text-left text-sm text-stone-700 truncate hover:text-stone-900"
                      >
                        {titleOf(post, t)}
                      </button>

                      {firstCard && (
                        <button
                          onClick={() => onPreview?.(post)}
                          className="relative flex-shrink-0 rounded-md focus:outline-none focus:ring-2 focus:ring-fox-fire"
                          title={cardHint}
                          aria-label={cardCount > 1
                            ? t(`Karussell — ${cardHint}, Vorschau öffnen`, `Carousel — ${cardHint}, open preview`)
                            : t("Bild hinterlegt, Vorschau öffnen", "Image attached, open preview")}
                        >
                          <img
                            src={mediaSrc(firstCard)}
                            alt=""
                            loading="lazy"
                            className="w-9 h-9 rounded-md object-cover border border-stone-200"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                          {cardCount > 1 && (
                            <span className="absolute -bottom-1 -right-1 text-[9px] leading-none px-1 py-0.5 rounded bg-stone-800/85 text-white font-semibold tabular-nums">
                              {cardCount}
                            </span>
                          )}
                        </button>
                      )}

                      {pf && (
                        <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${pf.cls}`}>
                          {pf.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {day && (
                <button
                  onClick={() => onAddPost?.(day)}
                  data-testid="prepared-post-add"
                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-stone-400 hover:text-fox-fire transition-colors px-2 py-1"
                >
                  <Plus size={13} />
                  {t("Eigenen Beitrag hinzufügen", "Add your own post")}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Umschalter für die vergangenen Tage ── */}
      {collapsible && (
        <button
          onClick={() => setShowAll(v => !v)}
          className="w-full flex items-center justify-center gap-1.5 px-5 py-2.5 text-xs font-medium text-stone-500 hover:text-stone-700 hover:bg-stone-50 border-t border-stone-100 transition-colors"
        >
          <ChevronDown size={13} className={showAll ? "rotate-180 transition-transform" : "transition-transform"} />
          {showAll ? t("Nur kommende Tage zeigen", "Show upcoming days only") : t(`Alle ${groups.length} Tage anzeigen`, `Show all ${groups.length} days`)}
        </button>
      )}
    </div>
  );
}
